
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Upload, Trash2 } from "lucide-react";

interface ProfileAvatarProps {
  avatarUrl: string | undefined;
  setAvatarUrl: (url: string) => void;
  userInitial: string;
}

export const ProfileAvatar = ({ avatarUrl, setAvatarUrl, userInitial }: ProfileAvatarProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setIsUploading(true);
      const file = event.target.files?.[0];
      if (!file) return;

      const fileExt = file.name.split('.').pop();
      const filePath = `${user?.id}-${Math.random()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', user?.id);

      if (updateError) throw updateError;

      setAvatarUrl(publicUrl);
      toast({
        title: "Avatar updated successfully",
        className: "bg-card border-none text-foreground",
      });
    } catch (error) {
      toast({
        title: "Error uploading avatar",
        className: "bg-card border-none text-foreground",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteAvatar = async () => {
    try {
      setIsDeleting(true);
      
      const { error } = await supabase
        .from('profiles')
        .update({ avatar_url: null })
        .eq('id', user?.id);

      if (error) throw error;

      setAvatarUrl('');
      toast({
        title: "Avatar removed successfully",
        className: "bg-card border-none text-foreground",
      });
    } catch (error) {
      toast({
        title: "Error removing avatar",
        className: "bg-card border-none text-foreground",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="flex flex-col items-center gap-4 mb-8">
      <Avatar className="h-32 w-32">
        <AvatarImage src={avatarUrl} />
        <AvatarFallback className="text-3xl text-foreground bg-card">
          {userInitial}
        </AvatarFallback>
      </Avatar>
      <div className="flex gap-2">
        <div>
          <Input
            id="avatar"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleAvatarUpload}
            disabled={isUploading}
          />
          <Button 
            variant="outline" 
            size="sm"
            className="flex items-center gap-2"
            onClick={() => document.getElementById('avatar')?.click()}
            disabled={isUploading}
          >
            {isUploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            Change picture
          </Button>
        </div>
        
        <Button 
          variant="outline" 
          size="sm"
          className="flex items-center gap-2 text-destructive border-destructive/30 hover:bg-destructive/10"
          onClick={handleDeleteAvatar}
          disabled={isDeleting || !avatarUrl}
        >
          {isDeleting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
          Delete picture
        </Button>
      </div>
    </div>
  );
};
